import {
  type LegacyRiskGapStatus,
  type RiskGapModel,
  isRiskGapUserStatus,
} from '@ctw/shared/api/fhir/models/risk-gap';
import { Badge } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { notify } from '@ctw/shared/components/toast';
import { confidenceLevelToRating } from '@ctw/shared/content/risk/risk-gap-confidence-level';
import { RiskGapDisclaimer } from '@ctw/shared/content/risk/risk-gap-disclaimer';
import { RiskGapInfoIcon } from '@ctw/shared/content/risk/risk-gap-info-icon';
import {
  RiskGapStatusDialogContent,
  type RiskGapStatusDialogStatus,
} from '@ctw/shared/content/risk/risk-gap-status-dialog';
import { isOpenGap } from '@ctw/shared/content/risk/utils';
import {
  RiskGapSummaryNoteContextProvider,
  useRiskGapSummaryNoteContext,
} from '@ctw/shared/context/controlled-text-context';
import { type CtwFetchFn, useCTW } from '@ctw/shared/context/ctw-context';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { getStandaloneBaseUrl } from '@ctw/shared/utils/environments';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { faArrowLeft, faFire, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { RISK_GAP_VALUES } from '@zeus-health/types';
import { useEffect, useMemo, useRef } from 'react';
import { ReactJason } from 'react-jason';
import { RiskGapEvidence } from './risk-gap-evidence';
import { RiskGapStatusFormV2 } from './risk-gap-status-form-v2';
import { riskGapTypeBadgeDefinition } from './risk-gap-type-badge-definition';

export interface RiskGapDetailViewProps {
  riskGap: RiskGapModel;
  onClose?: () => void;
}

function toRiskGapStatusDialogStatus(legacy: LegacyRiskGapStatus): RiskGapStatusDialogStatus {
  return isRiskGapUserStatus(legacy) ? legacy : 'open-gap';
}

function useConfidenceBadge(
  confidenceLevel: number | null,
  riskGapType: 'recapture' | 'suspect' | 'net-new' | 'unknown',
):
  | {
      content: 'High confidence' | 'Medium confidence' | 'Low confidence';
    }
  | undefined {
  return useMemo(() => {
    // Only suspect risk gaps have a displayed confidence level
    const confidenceRating =
      riskGapType === 'suspect' ? confidenceLevelToRating(confidenceLevel) : undefined;
    if (confidenceRating === 'high') {
      return { content: 'High confidence' };
    }
    if (confidenceRating === 'medium') {
      return { content: 'Medium confidence' };
    }
    if (confidenceRating === 'low') {
      return { content: 'Low confidence' };
    }
    return undefined;
  }, [confidenceLevel, riskGapType]);
}

/** Risk Gap Side Panel Detail view used exclusively in v2 of the UI */
export const RiskGapDetailView = ({ riskGap, onClose }: RiskGapDetailViewProps) => {
  // This is the highest level that cares about shared note state
  return (
    <RiskGapSummaryNoteContextProvider>
      <RiskGapDetailViewWithContext riskGap={riskGap} onClose={onClose} />
    </RiskGapSummaryNoteContextProvider>
  );
};

const RiskGapDetailViewWithContext = ({ riskGap, onClose }: RiskGapDetailViewProps) => {
  const { controlledText: note, setControlledText } = useRiskGapSummaryNoteContext({
    initialValue: riskGap.lastStatusUpdate.note,
  });
  const currentRiskGapId = useRef(riskGap.id);
  useEffect(() => {
    // reset the controlled note text when we focus a different risk gap
    if (currentRiskGapId.current !== riskGap.id) {
      currentRiskGapId.current = riskGap.id;
      setControlledText(riskGap.lastStatusUpdate.note);
    }
  }, [riskGap.id, setControlledText, riskGap.lastStatusUpdate.note]);
  const { breakpoints } = useBreakpointQuery();
  const { requestContext, ctwFetch } = useCTW();
  const queryClient = useQueryClient();
  const typeBadge = riskGapTypeBadgeDefinition(riskGap.type);
  const confidenceBadge = useConfidenceBadge(riskGap.confidenceLevel, riskGap.type);

  const isNoteEditable = isOpenGap(riskGap.status);
  const hasFirstPartyData = riskGap.icd10Codes.some((code) =>
    code.dataSourceType.includes('first-party'),
  );
  const statusSectionClassName = twx('p-4', {
    'bg-info-background': riskGap.type === 'net-new',
    'bg-caution-background': riskGap.type === 'suspect',
    'bg-success-background': riskGap.type === 'recapture',
  });
  const telemetry = useTelemetry();
  const { openModal: openViewFHIRModal } = useModal({
    boundaryName: 'ViewFHIRModal',
    layout: { fullbleed: false, mode: 'full', overflowBehavior: 'scroll', showCloseButton: true },
  });
  const { openModal: openStatusEditModal, closeModal: closeStatusEditModal } = useModal({
    boundaryName: 'StatusDialogModal',
    layout: { fullbleed: false, mode: 'fit', overflowBehavior: 'visible', showCloseButton: false },
  });

  const handleViewFHIR = () => {
    openViewFHIRModal({
      telemetry,
      action: {
        content: {
          body: (
            <>
              <Heading level="h2" className={tw`text-lg`}>
                View FHIR
              </Heading>
              <div className={tw`h-full text-sm [&_pre]:overflow-auto`}>
                <ReactJason value={riskGap.resource} />
              </div>
            </>
          ),
        },
      },
    });
  };

  const handleEditSubstatuses = () => {
    openStatusEditModal({
      telemetry,
      action: {
        content: {
          body: (
            <RiskGapStatusDialogContent
              note={note ?? ''}
              status={toRiskGapStatusDialogStatus(riskGap.status)}
              selectedSubstatuses={riskGap.substatuses}
              onConfirm={async (pendingStatus, substatuses, pendingNote) => {
                setControlledText(pendingNote);
                await handleSubmit(pendingNote, pendingStatus, substatuses);
                closeStatusEditModal();
              }}
              onCancel={closeStatusEditModal}
            />
          ),
        },
      },
    });
  };

  const handleSubmit = (
    notes: string,
    status: RiskGapStatusDialogStatus,
    substatuses: Array<string> = [],
  ) => {
    return new Promise<void>((resolve, reject) => {
      void ctwFetch(
        `${getStandaloneBaseUrl(requestContext.env)}/api/risk-gaps/${riskGap.id}/status-updates`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${requestContext.authToken}`,
          },
          body: JSON.stringify({
            data: {
              type: 'risk-gap-status-update',
              attributes: {
                status,
                substatuses,
                note: notes,
              },
            },
          }),
        },
      )
        .then((fetchResponse: Awaited<ReturnType<CtwFetchFn>>) => {
          if (fetchResponse.ok) {
            notify({
              type: 'success',
              title: 'The status has been updated.',
            });
          } else {
            telemetry.trackError({
              error: new Error('Error updating Risk Gap status'),
              context: {
                data: JSON.stringify(fetchResponse.data),
                headers: JSON.stringify(fetchResponse.headers),
              },
            });
            notify({
              type: 'error',
              title: 'Unable to update the risk gap status.',
              body: 'Please try again later or contact support if the issue persists.',
            });
          }
          void queryClient.invalidateQueries();
          resolve();
        })
        .catch((error) => {
          telemetry.trackError({
            error,
          });
          notify({
            type: 'error',
            title: 'Unable to update the risk gap status.',
            body: 'Please try again later or contact support if the issue persists.',
          });
          reject(error as Error);
        });
    });
  };

  return (
    <div
      data-testid="detail-view"
      className={twx('flex h-full max-h-full w-0 flex-col overflow-hidden bg-white', {
        'w-full max-w-full': riskGap,
      })}
    >
      <div
        className={tw`flex min-h-[65px] w-full items-center justify-between gap-2 overflow-visible border-b border-b-background-subtle px-5 py-3`}
      >
        <div
          className={tw`order-1 flex flex-1 flex-col items-start gap-2 md:order-0 lg:flex-row lg:items-center`}
        >
          <h2
            className={tw`order-1 flex font-normal text-content-main sm:text-sm md:text-md lg:order-0 lg:text-lg`}
          >
            {riskGap.title} (HCC {riskGap.hccCode})
          </h2>
          <div className={tw`order-0 flex flex-wrap items-center gap-2 lg:order-1`}>
            <Badge {...typeBadge} size="sm" />
          </div>
        </div>
        {onClose && (
          <Button
            ariaLabel={breakpoints.isAtLeast.lg ? 'Close' : 'Back'}
            type="button"
            size="sm"
            variant="circular"
            className={tw`order-0 flex min-w-8 items-center justify-center rounded-full bg-white p-2 text-content-subtle shadow-white hover:bg-white lg:order-1`}
            onClick={onClose}
          >
            <FontAwesomeIcon icon={breakpoints.isAtLeast.lg ? faXmark : faArrowLeft} />
          </Button>
        )}
        <Button
          ariaLabel="View FHIR"
          type="button"
          size="sm"
          variant="link"
          className={tw`order-2 flex rounded-b border p-2 text-content-subtle hover:text-content-main`}
          onClick={handleViewFHIR}
          telemetryInteractionName="resource_action_click"
        >
          <FontAwesomeIcon icon={faFire} className={tw`h-4 w-4`} />
        </Button>
      </div>
      {/* Content */}
      <div className={tw`flex flex-1 flex-col overflow-auto `}>
        <div className={`${statusSectionClassName} border-b border-b-background-subtle`}>
          {(confidenceBadge || !isNoteEditable) && (
            <div className={tw`mb-4 flex flex-wrap items-center gap-2`}>
              {confidenceBadge && (
                <Badge
                  className={tw`rounded-full border bg-white text-content-main`}
                  content={confidenceBadge.content}
                  icon={undefined}
                  size="sm"
                  variant="meta"
                />
              )}
              {!isNoteEditable && (
                <div className={tw`flex flex-grow justify-end`}>
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className={tw`text-content-main`}
                    onClick={handleEditSubstatuses}
                  >
                    <FontAwesomeIcon icon={faPencil} className={tw`relative top-[-1] mr-1 h-3`} />
                    Edit Status
                  </Button>
                </div>
              )}
            </div>
          )}
          {/* ICD-10 Codes */}
          {riskGap.icd10Codes.length > 0 && (
            <div className={tw`mb-4 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2`}>
              {riskGap.icd10Codes.map((code) => {
                const isPayerSource = code.sources.includes(
                  RISK_GAP_VALUES.MEASURE_REPORT.GROUP.CODE.CODING.CODE.EXTENSION.RISK_GAP_SOURCE
                    .PAYER_GAP,
                );
                return (
                  <div
                    key={code.code}
                    className={tw`flex h-full flex-col gap-1.5 rounded-lg border border-divider-main bg-white p-2.5`}
                  >
                    <div className={tw`text-sm leading-snug`}>
                      <span className={tw`font-bold`}>{code.code}</span>{' '}
                      {code.display && <span>{code.display}</span>}
                    </div>
                    {isPayerSource && (
                      <div className={tw`text-content-subtle text-xs`}>Reported by payer</div>
                    )}
                    {code.lastEncounterDiagnosisRecordedDate && (
                      <div className={tw`flex gap-1.5`}>
                        <RiskGapInfoIcon
                          hide={!hasFirstPartyData}
                          info={'First Party Data'}
                          className={tw`flex items-center`}
                        />
                        <div className={tw`mt-auto text-content-subtle text-xs`}>
                          Encounter Diagnosis Found{' '}
                          {formatFHIRDate(code.lastEncounterDiagnosisRecordedDate)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <RiskGapStatusFormV2
            riskGap={riskGap}
            currentStatus={riskGap.status}
            onSave={handleSubmit}
          />
        </div>
        <div className={tw`p-4`}>
          <RiskGapEvidence riskGap={riskGap} />
        </div>
        <div className={tw`mb-4 flex flex-col gap-4 px-4`}>
          <RiskGapDisclaimer />
        </div>
      </div>
    </div>
  );
};
